// src/services/api.js - COMPLETE API SERVICE with Blog Integration
import axios from 'axios';
import { auth } from '../config/firebase';

// Drops undefined/null/'' values before building a query string, so an unset
// filter is genuinely omitted rather than sent as the literal string
// "undefined" (which URLSearchParams would otherwise produce).
function buildCleanParams(params) {
  const clean = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      clean[key] = value;
    }
  });
  return new URLSearchParams(clean);
}

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://www.game2earn.co.uk',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor to add Firebase ID token
api.interceptors.request.use(
  async (config) => {
    try {
      // Get current Firebase user and their ID token
      const currentUser = auth.currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (error) {
      console.error('Error getting Firebase ID token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const currentUser = auth.currentUser;
        if (currentUser) {
          const token = await currentUser.getIdToken(true);
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Let the AuthContext handle this
      }
    }

    return Promise.reject(error);
  }
);

// ============= MAIN API SERVICE =============
export const apiService = {
  // Health check endpoint
  health: () => api.get('/api/health'),

  // Debug endpoint to check registered routes
  debug: {
    routes: () => api.get('/api/debug/routes'),
  },

  // ============= AUTHENTICATION =============
  auth: {
    // Register user profile after Firebase signup
    register: (userData) => {
      console.log('🔄 Registering user:', userData);
      return api.post('/api/auth/register', userData);
    },
    
    // Verify Firebase ID token and get user profile
    verifyToken: () => {
      console.log('🔄 Verifying token...');
      return api.post('/api/auth/verify-token');
    },

    checkLocation: (email) => api.post('/api/auth/check-location', { email }),
    
    // Get user profile
    getProfile: () => api.get('/api/auth/profile'),

    updateProfile: (data) => {
      console.log('📄 Updating profile:', data);
      return api.put('/api/auth/profile', data);
    },
    
    // Logout (simple)
    logout: () => api.post('/api/auth/logout'),
    
    // Password reset (handled by Firebase, but log it)
    forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),

    syncVerification: () => api.post('/api/auth/sync-verification')
  },
  

// ============= USER MANAGEMENT =============
users: {
  getProfile: () => api.get('/api/users/profile'),
  
/*   updateProfile: (data) => {
    console.log('📄 Updating profile:', data);
    return api.put('/api/users/profile', data);
  }, */
  getProgress: (page = 1, per_page = 20) =>
  api.get(`/api/users/progress?page=${page}&per_page=${per_page}`),
  // Enhanced earnings with offer details
  getEarnings: (page = 1, perPage = 20) => 
      api.get(`/api/users/earnings?page=${page}&per_page=${perPage}`),
  
  // New: Recent earnings for dashboard
  getRecentEarnings: (limit = 10) =>
      api.get(`/api/users/earnings/recent?limit=${limit}`),
  
  getPayouts: (page = 1, perPage = 10) =>
    api.get(`/api/users/payouts?page=${page}&per_page=${perPage}`),
  
  // Get user statistics
  getStats: () => api.get('/api/users/stats'),
  
  // FIXED: Single payout function with correct endpoint
  requestPayout: async (payoutData) => {
    try {
      console.log('📄 Requesting payout:', payoutData);
      const response = await api.post('/api/users/payout', payoutData);
      return response;
    } catch (error) {
      console.error('Request payout error:', error);
      throw error;
    }
  },

  // Cancel a payout
  cancelPayout: async (payoutId) => {
    try {
      const response = await api.post(`/api/users/payout/${payoutId}/cancel`);
      return response;
    } catch (error) {
      console.error('Cancel payout error:', error);
      throw error;
    }
  },

  // Get detailed balance
  getDetailedBalance: async () => {
    try {
      const response = await api.get('/api/users/balance/detailed');
      return response;
    } catch (error) {
      console.error('Get detailed balance error:', error);
      throw error;
    }
  },

  // Get available earnings for payout
  getAvailableEarnings: async (page = 1, perPage = 50) => {
    try {
      // Filter for approved earnings without payout_id
      const response = await api.get(`/api/users/earnings?page=${page}&per_page=${perPage}&status=approved&available_for_payout=true`);
      return response;
    } catch (error) {
      console.error('Get available earnings error:', error);
      throw error;
    }
  },

  // Get user balance
  getBalance: async () => {
    try {
      const response = await api.get('/api/users/balance');
      return response;
    } catch (error) {
      console.error('Get balance error:', error);
      throw error;
    }
  }
},

  // ============= OFFERS MANAGEMENT =============
  offers: {
    getAll: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return api.get(`/api/offers/alloffers${queryString ? `?${queryString}` : ''}`);
    },
    
    getFeatured: (limit = 6) => {
      return api.get(`/api/offers/featured?limit=${limit}`);
    },

    getFeaturedGiftCards: () => {
      return api.get(`/api/offers/featured-gift-cards`);
    },

      getUserBalance: (userId) => {
    console.log(`🔄 Getting user balance: ${userId}`);
    return api.get(`/api/offers/balance/${userId}`);
  },
    
    getDetails: (offerId) => {
      console.log(`🔄 Getting offer details: ${offerId}`);
      return api.get(`/api/offers/${offerId}`);
    },
    
    start: (offerId) => {
      console.log(`🔄 Starting offer: ${offerId}`);
      return api.post(`/api/offers/${offerId}/start`);
    },

    // Submit a survey-offer completion (answers = { questionId: answer })
    submitSurvey: (offerId, answers) => {
      console.log(`🔄 Submitting survey: ${offerId}`);
      return api.post(`/api/offers/${offerId}/survey/submit`, { answers });
    },

    // Complete a social-media offer - one click, instantly credited
    completeSocialOffer: (offerId) => {
      console.log(`🔄 Completing social offer: ${offerId}`);
      return api.post(`/api/offers/${offerId}/social/complete`);
    },

    updateProgress: (offerId, progress) => {
      console.log(`🔄 Updating progress for ${offerId}:`, progress);
      return api.put(`/api/offers/${offerId}/progress`, progress);
    },

     getSiteStats: () => {
    console.log('Getting site stats for landing page...');
    return api.get('/api/offers/sitestats');
  },
    
    getCategories: () => api.get('/api/offers/categories/list'),
    
    // Debug endpoint for offers
    debug: () => api.get('/api/offers/debug'),
  },

  blogs: {
    // Get all published blogs with pagination
    getAll: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      console.log('🔄 Getting all blogs with params:', params);
      return api.get(`/api/blogs${queryString ? `?${queryString}` : ''}`);
    },
    
    // Get featured blogs for navigation dropdown
    getFeatured: (limit = 5) => {
      return api.get(`/api/blogs/featured?limit=${limit}`);
    },
    
    // Get individual blog post by slug
    getBySlug: (slug) => {
      console.log(`🔄 Getting blog by slug: ${slug}`);
      return api.get(`/api/blogs/${slug}`);
    },
  },

  // ============= ADMIN PANEL =============
admin: {
  // Dashboard statistics
  getStats: (params = {}) => {
    console.log('🔄 Getting admin stats...', params);
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/api/admin/stats${queryString ? `?${queryString}` : ''}`);
  },

  getVisitorJourney: () => api.get('/api/admin/analytics/visitor-journey'),
  getUserJourney: () => api.get('/api/admin/analytics/user-journey'),
  getDailyTrends: (days = 7) => api.get(`/api/admin/analytics/daily-trends?days=${days}`),
  // Push notification open-rate (sent vs. tapped/opened), by notification_type,
  // scoped to a date_from/date_to range (falls back to the last `days` days
  // server-side when no range is given).
  getNotificationAnalytics: (params = {}) => api.get(`/api/admin/analytics/notifications?${new URLSearchParams(params)}`),

  // App update prompt (Dashboard banner / force-update gate on the mobile app)
  getAppVersionConfig: (platform = 'android') => api.get(`/api/admin/app-version/${platform}`),
  updateAppVersionConfig: (platform, data) => api.put(`/api/admin/app-version/${platform}`, data),

  // App controls: live feature switches per platform (see AppControlsCard in admin analytics)
  getAppFlags: () => api.get('/api/admin/app-flags'),
  setAppFlag: (key, platform, enabled) => api.put(`/api/admin/app-flags/${key}/${platform}`, { enabled }),

  // ===== AD SPEND (Google Ads + Meta Ads) =====
  // Each accepts optional { start_date, end_date, include_today }
  googleAds: {
    testConnection: () => api.get('/api/admin/google-ads/test-connection'),
    getSummary: (params = {}) => api.get(`/api/admin/google-ads/summary?${new URLSearchParams(params)}`),
    getCampaigns: (params = {}) => api.get(`/api/admin/google-ads/campaigns?${new URLSearchParams(params)}`),
    getKeywords: (params = {}) => api.get(`/api/admin/google-ads/keywords?${new URLSearchParams(params)}`),
    getDaily: (params = {}) => api.get(`/api/admin/google-ads/daily?${new URLSearchParams(params)}`),
  },
  metaAds: {
    testConnection: () => api.get('/api/admin/meta-ads/test-connection'),
    getSummary: (params = {}) => api.get(`/api/admin/meta-ads/summary?${new URLSearchParams(params)}`),
    getCampaigns: (params = {}) => api.get(`/api/admin/meta-ads/campaigns?${new URLSearchParams(params)}`),
    getDaily: (params = {}) => api.get(`/api/admin/meta-ads/daily?${new URLSearchParams(params)}`),
  },

  // ===== GOOGLE ADS AGENT (hourly tick -> pause schedule / daily brief / weekly review) =====
  googleAgent: {
    getOverview: () => api.get('/api/admin/google-agent/overview'),
    getAssets: () => api.get('/api/admin/google-agent/assets'),
    approve: (id, note) => api.post(`/api/admin/google-agent/proposals/${id}/approve`, { note }),
    reject: (id, note) => api.post(`/api/admin/google-agent/proposals/${id}/reject`, { note }),
    markApplied: (id) => api.post(`/api/admin/google-agent/proposals/${id}/applied`, {}),
    // Turning live ON must be confirmed; turning it OFF is a kill switch (restores a campaign the agent paused).
    setLive: (enabled) => api.post('/api/admin/google-agent/settings', { live_execution_enabled: enabled, confirm: enabled }),
    runNow: (job) => api.post('/api/admin/google-agent/run', { job }),
    sendTestEmail: () => api.post('/api/admin/google-agent/test-email', {}),
  },

  // Signup device split, app-only ad spend by device, and per-acquisition-
  // channel users/revenue/cost/margin. Params: { date_from, date_to } (YYYY-MM-DD).
  getAcquisitionSummary: (params = {}) => api.get(`/api/admin/acquisition-summary?${new URLSearchParams(params)}`),

  // ===== DAILY TRAFFIC TRACKER =====
  trafficTracker: {
    // params: { date_from, date_to, group_by } - group_by is 'day' | 'week' | 'month'
    getTable: (params = {}) => api.get(`/api/admin/traffic-tracker?${new URLSearchParams(params)}`),
    // Upserts the manual Other Spend / Notes fields for one calendar day (YYYY-MM-DD)
    updateEntry: (dateStr, data) => api.put(`/api/admin/traffic-tracker/${dateStr}`, data),
  },

  // ===== COHORT ANALYSIS =====
  // Shared filter shape across all four calls: { date_from, date_to, platform,
  // source, campaign, country, channel } - all optional. undefined/null/''
  // values are dropped before building the query string so an unset filter
  // is genuinely omitted rather than sent as the literal string "undefined".
  cohorts: {
    // params adds: group_by ('day'|'week'|'month', default 'week')
    getOverview: (params = {}) => api.get(`/api/admin/cohorts?${buildCleanParams(params)}`),
    // params adds: group_by, cohort_keys (comma-separated), split_by ('source'|'platform')
    getCurves: (params = {}) => api.get(`/api/admin/cohorts/curves?${buildCleanParams(params)}`),
    // params adds: page, per_page
    getUsers: (params = {}) => api.get(`/api/admin/cohorts/users?${buildCleanParams(params)}`),
    getCac: (params = {}) => api.get(`/api/admin/cohorts/cac?${buildCleanParams(params)}`),
    getFilterOptions: () => api.get('/api/admin/cohorts/filters'),
  },

  // ===== AI DATA ANALYST =====
  aiAnalyst: {
    // Exploratory questions run up to AI_ANALYST_MAX_TOOL_ITERATIONS (15)
    // sequential Claude round-trips, each potentially firing several DB
    // queries or live ad-API calls - observed taking 40-90+ seconds even at
    // the old cap of 6, well past the 30s default timeout on the shared
    // `api` instance above (which was silently killing these requests
    // client-side while the backend kept working and completed successfully
    // seconds later - the two disagreeing is what looked like "breaking").
    // Override with a much longer timeout for this call only, with headroom
    // above the higher iteration cap.
    // attachments: [{ name, data (base64) }] - files for this question only; the backend reads
    // them in memory and never stores them, so the page re-sends them with each follow-up.
    ask: (question, history = [], chatId = null, isPrivate = false, attachments = []) =>
      api.post('/api/admin/ai-analyst/ask', { question, history, chat_id: chatId, is_private: isPrivate, attachments }, { timeout: 240000 }),
    // Saved-chat history (2026-08-27) - listChats never returns private
    // chats (backend-enforced, not a frontend filter), and getChat 404s on
    // a private chat id too, so there's no path in this client that can
    // ever surface one.
    listChats: () => api.get('/api/admin/ai-analyst/chats'),
    getChat: (chatId) => api.get(`/api/admin/ai-analyst/chats/${chatId}`),
  },

  // ===== USER MANAGEMENT =====
  getUsers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/api/admin/users${queryString ? `?${queryString}` : ''}`);
  },
  // ===== USER EARNINGS MANAGEMENT ===== 
getUserEarnings: async (userId) => {
  try {
    console.log('🔍 Admin: Getting user earnings for:', userId);
    const response = await api.get(`/api/admin/users/${userId}/earnings`);
    return response;
  } catch (error) {
    console.error('⛔ Get user earnings error:', error);
    throw error;
  }
},
  getUserDetails: (userId) => api.get(`/api/admin/users/${userId}`),

  // Aggregated analytics for the game2earn MOBILE APP (first-party tracking).
  // section: overview | funnels | offers | behaviour | health.
  // params: date_from, date_to (YYYY-MM-DD, inclusive), platform (all|android|ios), app_version.
  getAppAnalytics: (section, params = {}) =>
    api.get(`/api/admin/app-analytics/${section}?${buildCleanParams(params)}`),

  // Everything a user did in the game2earn MOBILE APP (first-party tracking -
  // not the website), grouped by session. Params: session_limit, before.
  getUserAppJourney: (userId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/api/admin/users/${userId}/app-journey${queryString ? `?${queryString}` : ''}`);
  },

  // Event mapping (original/adjusted amounts) for a specific offer
  getOfferEvents: async (apiId) => {
    try {
      console.log('🔍 Admin: Getting offer events for:', apiId);
      const response = await api.get(`/api/admin/offers/${apiId}/events`);
      return response;
    } catch (error) {
      console.error('⛔ Get offer events error:', error);
      throw error;
    }
  },

  // Search offers by name/id, independent of the Offer Performance table's
  // top-20-by-views ranking - lets an admin open any offer's insight modal
  // even if it never makes that leaderboard.
  searchOffers: async (query) => {
    try {
      const response = await api.get(`/api/admin/offers/search?q=${encodeURIComponent(query)}`);
      return response;
    } catch (error) {
      console.error('⛔ Search offers error:', error);
      throw error;
    }
  },

  // Resolve event_id -> {offer_id, offer_name, event_name} in one batch call.
  // Used by the survey milestone-lock editor to label previously-saved
  // prerequisite_event_ids with a friendly "Game - Event" name.
  lookupEvents: async (eventIds) => {
    try {
      const response = await api.get(`/api/admin/events/lookup?event_ids=${encodeURIComponent(eventIds.join(','))}`);
      return response;
    } catch (error) {
      console.error('⛔ Lookup events error:', error);
      throw error;
    }
  },

  // Per-offer insight dashboard (funnel + per-event conversion + revenue/margin + trend)
  getOfferInsights: async (apiId, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      console.log('🔍 Admin: Getting offer insights for:', apiId, params);
      const response = await api.get(`/api/admin/offers/${apiId}/insights${queryString ? `?${queryString}` : ''}`);
      return response;
    } catch (error) {
      console.error('⛔ Get offer insights error:', error);
      throw error;
    }
  },

  // Edit a single offer event. Pass only the fields you want to change —
  // omitted fields are left untouched by the backend:
  //   is_manual_adjusted=true  -> lock adjusted_amount (sync skips 30% recompute)
  //   is_manual_adjusted=false -> release the lock
  //   is_active=false          -> callbacks for this event stop being credited
  updateOfferEvent: async (apiId, eventId, { adjusted_amount, is_manual_adjusted, is_active } = {}) => {
    console.log(`💲 Admin: Updating event ${apiId}/${eventId}`, { adjusted_amount, is_manual_adjusted, is_active });
    return api.put(`/api/admin/offers/${apiId}/events/${eventId}`, {
      adjusted_amount,
      is_manual_adjusted,
      is_active,
    });
  },

  // Read-only pricing suggestion for every event of one offer (nothing is saved).
  suggestOfferPricing: async (apiId, targetMargin) => {
    return api.get(`/api/admin/offers/${apiId}/suggest-pricing`, {
      params: targetMargin != null ? { target_margin: targetMargin } : undefined,
    });
  },

  updateUser: (userId, data) => {
    console.log(`🔄 Updating user ${userId}:`, data);
    return api.put(`/api/admin/users/${userId}`, data);
  },

  adjustUserBalance: (userId, data) => {
    console.log(`💰 Adjusting balance for user ${userId}:`, { ...data, password: '***' });
    return api.post(`/api/admin/users/${userId}/adjust-balance`, data);
  },

  // Turn the game tier system off (disabled=true) or back on for one user.
  setUserTierLock: (userId, disabled) =>
    api.put(`/api/admin/users/${userId}/tier-lock`, { disabled }),

  // Permanently delete a user's account (DB rows + Firebase Auth). Password-gated,
  // same shared admin password as adjustUserBalance.
  deleteUser: (userId, data) => {
    console.log(`🗑️ Deleting user ${userId}:`, { ...data, password: '***' });
    return api.post(`/api/admin/users/${userId}/delete`, data);
  },


  banUser: (userId, reason) => {
    console.log(`🔄 Banning user ${userId}:`, reason);
    return api.post(`/api/admin/users/${userId}/ban`, { reason });
  },
  
  // ===== OFFER MANAGEMENT ===== (FIXED - no duplicates)
  getOffers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    console.log('🔄 Getting admin offers with params:', params);
    return api.get(`/api/admin/offers${queryString ? `?${queryString}` : ''}`);
  },

  updateOfferMedia: (offerId, mediaData) => {
    console.log(`🖼️ Update offer media: ${offerId}`, mediaData);
    return api.put(`/api/admin/offers/${offerId}/media`, mediaData);
  },

  updateOfferAnchor: (offerId, anchorData) => {
    console.log(`🔄 Update offer anchor: ${offerId}`, anchorData);
    return api.put(`/api/admin/offers/${offerId}/anchor`, anchorData);
  },
  
  updateOfferFeatured: (offerId, featured) => {
    console.log(`🔄 Toggle offer featured: ${offerId} -> ${featured}`);
    return api.put(`/api/admin/offers/${offerId}/featured?featured=${featured}`);
  },
  
updateOfferStatus: (offerId, isActive) => {
    console.log(`🔄 Toggle offer active: ${offerId} -> ${isActive}`);
    return api.put(`/api/admin/offers/${offerId}/status?new_status=${isActive}`);  // ← Query parameter
},
  
  createOffer: (data) => {
    console.log('🔄 Creating offer:', data);
    return api.post('/api/admin/offers', data);
  },

  // ===== SURVEY MANAGEMENT =====
  createSurvey: (data) => {
    console.log('🔄 Creating survey:', data);
    return api.post('/api/admin/surveys', data);
  },

  getSurvey: (offerId) => {
    console.log(`🔄 Getting survey: ${offerId}`);
    return api.get(`/api/admin/surveys/${offerId}`);
  },

  listSurveys: () => {
    console.log('🔄 Listing surveys');
    return api.get('/api/admin/surveys');
  },

  updateSurvey: (offerId, data) => {
    console.log(`🔄 Updating survey: ${offerId}`, data);
    return api.put(`/api/admin/surveys/${offerId}`, data);
  },

  // ===== SOCIAL MEDIA OFFER MANAGEMENT =====
  createSocialOffer: (data) => {
    console.log('🔄 Creating social offer:', data);
    return api.post('/api/admin/social-offers', data);
  },

  getSocialOffer: (offerId) => {
    console.log(`🔄 Getting social offer: ${offerId}`);
    return api.get(`/api/admin/social-offers/${offerId}`);
  },

  listSocialOffers: () => {
    console.log('🔄 Listing social offers');
    return api.get('/api/admin/social-offers');
  },

  updateSocialOffer: (offerId, data) => {
    console.log(`🔄 Updating social offer: ${offerId}`, data);
    return api.put(`/api/admin/social-offers/${offerId}`, data);
  },


  updateOffer: (offerId, data) => {
    console.log(`🔄 Updating offer ${offerId}:`, data);
    return api.put(`/api/admin/offers/${offerId}`, data);
  },
  
  deleteOffer: (offerId) => {
    console.log(`🔄 Deleting offer: ${offerId}`);
    return api.delete(`/api/admin/offers/${offerId}`);
  },
    
    // ===== EARNINGS & PAYOUTS =====
    getEarnings: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return api.get(`/api/admin/earnings${queryString ? `?${queryString}` : ''}`);
    },

/*     
    
    getPayouts: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return api.get(`/api/admin/payouts${queryString ? `?${queryString}` : ''}`);
    },
    
    approvePayout: (payoutId) => {
      console.log(`🔄 Approving payout: ${payoutId}`);
      return api.post(`/api/admin/payouts/${payoutId}/approve`);
    },
    
    rejectPayout: (payoutId, reason) => {
      console.log(`🔄 Rejecting payout ${payoutId}:`, reason);
      return api.post(`/api/admin/payouts/${payoutId}/reject`, { reason });
    }, */
     getAllPayouts: async (page = 1, perPage = 50, status = 'all') => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });
      
      if (status !== 'all') {
        params.append('status_filter', status);
      }
      
      console.log('🔄 Admin: Getting all payouts...');
      // ✅ FIXED: Correct endpoint with /api prefix
      const response = await api.get(`/api/admin/payouts?${params}`);
      return response;
    } catch (error) {
      console.error('❌ Get all payouts error:', error);
      throw error;
    }
  },
  // Process a payout (admin only)
  processPayout: async (payoutId, processingData) => {
    try {
      console.log('✅ Admin: Processing payout:', payoutId, processingData);
      // ✅ FIXED: Correct endpoint matching backend
      const response = await api.post(`/api/admin/payouts/${payoutId}/approve`, processingData);
      return response;
    } catch (error) {
      console.error('❌ Process payout error:', error);
      throw error;
    }
  },

  // Cancel a payout (admin)
  adminCancelPayout: async (payoutId, reason, emailOption = 'none') => {
    try {
      console.log('❌ Admin: Cancelling payout:', payoutId, reason, emailOption);
      // ✅ FIXED: Correct endpoint matching backend
      const response = await api.post(`/api/admin/payouts/${payoutId}/reject`, { reason, email_option: emailOption });
      return response;
    } catch (error) {
      console.error('❌ Cancel payout error:', error);
      throw error;
    }
  },

  // Create a Stripe Identity verification session for the payout's user and email
  // them the link. Informational only - does not change the payout's status; the
  // result comes back later via Stripe's webhook.
  sendPayoutStripeVerification: async (payoutId) => {
    try {
      console.log('🪪 Admin: Sending Stripe verification for payout:', payoutId);
      const response = await api.post(`/api/admin/payouts/${payoutId}/stripe-verification`);
      return response;
    } catch (error) {
      console.error('❌ Send Stripe verification error:', error);
      throw error;
    }
  },

  // Re-send the identity verification email for the payout's EXISTING Stripe
  // session (same verification_url, no new VerificationSession/charge).
  resendPayoutStripeVerification: async (payoutId) => {
    try {
      console.log('🪪 Admin: Resending Stripe verification for payout:', payoutId);
      const response = await api.post(`/api/admin/payouts/${payoutId}/stripe-verification/resend`);
      return response;
    } catch (error) {
      console.error('❌ Resend Stripe verification error:', error);
      throw error;
    }
  },

  // Standalone reminder nudge (7-day decline warning) for the payout's EXISTING
  // Stripe session - distinct email/wording from resendPayoutStripeVerification,
  // tracked separately, but also reuses the same link (no new session/charge).
  getPayoutHistory: (payoutId) => api.get(`/api/admin/payouts/${payoutId}/history`),

  remindPayoutStripeVerification: async (payoutId) => {
    try {
      console.log('🔔 Admin: Sending verification reminder for payout:', payoutId);
      const response = await api.post(`/api/admin/payouts/${payoutId}/stripe-verification/remind`);
      return response;
    } catch (error) {
      console.error('❌ Send verification reminder error:', error);
      throw error;
    }
  },


    // ===== BLOG MANAGEMENT =====
    blogs: {
      // Get all blogs (including drafts) for admin
      getAll: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        console.log('🔄 Getting admin blogs with params:', params);
        return api.get(`/api/admin/blogs${queryString ? `?${queryString}` : ''}`);
      },
      
      // Create new blog post
      create: (data) => {
        console.log('🔄 Creating blog:', data);
        return api.post('/api/admin/blogs', data);
      },
      
      // Get blog details for editing
      getDetails: (blogId) => {
        console.log(`🔄 Getting blog details: ${blogId}`);
        return api.get(`/api/admin/blogs/${blogId}`);
      },
      
      // Update existing blog post
      update: (blogId, data) => {
        console.log(`🔄 Updating blog ${blogId}:`, data);
        return api.put(`/api/admin/blogs/${blogId}`, data);
      },
      
      // Delete blog post
      delete: (blogId) => {
        console.log(`🔄 Deleting blog: ${blogId}`);
        return api.delete(`/api/admin/blogs/${blogId}`);
      },
      
      // Toggle published status
      updatePublished: (blogId, published) => {
        console.log(`🔄 Toggle blog published: ${blogId} -> ${published}`);
        return api.put(`/api/admin/blogs/${blogId}/published?published=${published}`);
      },
      
      // Toggle featured status
      updateFeatured: (blogId, featured) => {
        console.log(`🔄 Toggle blog featured: ${blogId} -> ${featured}`);
        return api.put(`/api/admin/blogs/${blogId}/featured?featured=${featured}`);
      },
      
      // Bulk operations
      bulkPublish: (blogIds) => {
        console.log('🔄 Bulk publishing blogs:', blogIds);
        return api.post('/api/admin/blogs/bulk/publish', { blog_ids: blogIds });
      },
      
      bulkUnpublish: (blogIds) => {
        console.log('🔄 Bulk unpublishing blogs:', blogIds);
        return api.post('/api/admin/blogs/bulk/unpublish', { blog_ids: blogIds });
      },
      
      bulkDelete: (blogIds) => {
        console.log('🔄 Bulk deleting blogs:', blogIds);
        return api.post('/api/admin/blogs/bulk/delete', { blog_ids: blogIds });
      },
    },
    
    // ===== CALLBACK MANAGEMENT =====
    callbacks: {
      // Get all callbacks with filtering
      getAll: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        console.log('🔄 Getting callbacks with params:', params);
        return api.get(`/api/admin/callbacks${queryString ? `?${queryString}` : ''}`);
      },
      
      // Get specific callback details
      getDetails: (callbackId) => {
        console.log(`🔄 Getting callback details: ${callbackId}`);
        return api.get(`/api/admin/callbacks/${callbackId}`);
      },
      
      // Reprocess a failed callback
      reprocess: (callbackId) => {
        console.log(`🔄 Reprocessing callback: ${callbackId}`);
        return api.post(`/api/admin/callbacks/${callbackId}/reprocess`);
      },
      
      // Get callback statistics
      getStats: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        console.log('🔄 Getting callback stats with params:', params);
        return api.get(`/api/admin/callbacks/stats${queryString ? `?${queryString}` : ''}`);
      },
      
      // Get callbacks for a specific user
      getByUser: (userId, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/api/admin/users/${userId}/callbacks${queryString ? `?${queryString}` : ''}`);
      },
      
      // Get callbacks for a specific offer
      getByOffer: (offerId, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/api/admin/offers/${offerId}/callbacks${queryString ? `?${queryString}` : ''}`);
      },
      
      // Export callbacks to CSV
      exportCsv: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/api/admin/callbacks/export${queryString ? `?${queryString}` : ''}`, {
          responseType: 'blob'
        });
      },
    },


    
  },

  // ============= BITLABS INTEGRATION =============
  bitlabs: {
    // Check BitLabs configuration status
    getStatus: () => {
      console.log('🔄 Getting BitLabs status...');
      return api.get('/api/bitlabs/status');
    },
    
    // Test BitLabs connection (admin only)
    testConnection: () => {
      console.log('🔄 Testing BitLabs connection...');
      return api.post('/api/bitlabs/test');
    },
    
    // Get recent callback activity (admin only)
    getRecentActivity: (hours = 24) => {
      console.log(`🔄 Getting BitLabs activity for last ${hours} hours...`);
      return api.get(`/api/bitlabs/activity?hours=${hours}`);
    },
    
    // Manual callback trigger (admin only, for testing)
    triggerCallback: (callbackData) => {
      console.log('🔄 Manually triggering callback:', callbackData);
      return api.post('/api/bitlabs/callback/trigger', callbackData);
    },
  }
};

// ============= HELPER FUNCTIONS =============

// Callback management helpers
export const callbackHelpers = {
  // Format callback state for display
  formatState: (state) => {
    const stateMap = {
      'COMPLETED': { label: 'Completed', color: 'green', icon: '✅' },
      'PENDING': { label: 'Pending', color: 'yellow', icon: '⏳' },
      'RECONCILED': { label: 'Reconciled', color: 'blue', icon: '🔄' },
      'NONE': { label: 'None', color: 'gray', icon: '⚪' }
    };
    return stateMap[state] || { label: state, color: 'gray', icon: '❓' };
  },

  // Format callback processing status
  formatProcessingStatus: (isProcessed, error) => {
    if (isProcessed && !error) {
      return { label: 'Success', color: 'green', icon: '✅' };
    } else if (isProcessed && error) {
      return { label: 'Failed', color: 'red', icon: '❌' };
    } else {
      return { label: 'Pending', color: 'yellow', icon: '⏳' };
    }
  },

  // Format currency values
  formatCurrency: (amount, currency = 'USD') => {
    if (amount === null || amount === undefined) return '$0.00';
    
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return formatter.format(amount);
  },

  // Format timestamp for display
  formatTimestamp: (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  },

  // Validate transaction ID format
  isValidTransactionId: (txId) => {
    // BitLabs transaction IDs typically follow patterns like 'o123456' or 'or789012'
    return /^o[r]?[0-9]+$/.test(txId);
  },

  // Extract offer ID from transaction ID if possible
  extractOfferIdFromTx: (txId) => {
    const match = txId.match(/^o[r]?([0-9]+)$/);
    return match ? match[1] : null;
  },

  // Generate filter options for callback management
  getFilterOptions: () => ({
    states: [
      { value: '', label: 'All States' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'PENDING', label: 'Pending' },
      { value: 'RECONCILED', label: 'Reconciled' },
      { value: 'NONE', label: 'None' }
    ],
    processingStatus: [
      { value: '', label: 'All Status' },
      { value: 'true', label: 'Processed' },
      { value: 'false', label: 'Unprocessed' }
    ],
    timeRanges: [
      { value: '', label: 'All Time' },
      { value: '1h', label: 'Last Hour' },
      { value: '24h', label: 'Last 24 Hours' },
      { value: '7d', label: 'Last 7 Days' },
      { value: '30d', label: 'Last 30 Days' }
    ]
  })
};

// Blog management helpers
export const blogHelpers = {
  // Format blog date for display
  formatDate: (dateString, format = 'long') => {
    if (!dateString) return 'Draft';
    
    const date = new Date(dateString);
    const options = {
      short: { month: 'short', day: 'numeric' },
      medium: { month: 'short', day: 'numeric', year: 'numeric' },
      long: { year: 'numeric', month: 'long', day: 'numeric' },
      full: { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    };
    
    return date.toLocaleDateString('en-US', options[format] || options.long);
  },

  // Format view count for display
  formatViews: (viewCount) => {
    if (!viewCount || viewCount < 1000) return (viewCount || 0).toString();
    if (viewCount < 1000000) return `${(viewCount / 1000).toFixed(1)}K`;
    return `${(viewCount / 1000000).toFixed(1)}M`;
  },

  // Generate slug from title
  generateSlug: (title) => {
    if (!title) return '';
    
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/[\s-]+/g, '-') // Replace spaces and multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  },

  // Estimate reading time
  estimateReadTime: (content) => {
    if (!content) return '0 min read';
    
    const wordsPerMinute = 200;
    const wordCount = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  },

  // Truncate content for previews
  truncateContent: (content, maxLength = 150) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    
    // Try to break at word boundary
    const truncated = content.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastSpace > maxLength * 0.8) {
      return truncated.substring(0, lastSpace) + '...';
    }
    
    return truncated + '...';
  },

  // Extract plain text from HTML content
  extractPlainText: (htmlContent) => {
    if (!htmlContent) return '';
    
    // Simple HTML tag removal (for basic cases)
    return htmlContent
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .replace(/&amp;/g, '&') // Replace &amp; with &
      .replace(/&lt;/g, '<') // Replace &lt; with <
      .replace(/&gt;/g, '>') // Replace &gt; with >
      .trim();
  },

  // Validate blog form data
  validateBlogData: (formData) => {
    const errors = [];

    // Required fields
    if (!formData.title?.trim()) {
      errors.push('Title is required');
    } else if (formData.title.length > 200) {
      errors.push('Title must be 200 characters or less');
    }

    if (!formData.preview?.trim()) {
      errors.push('Preview is required');
    } else if (formData.preview.length > 500) {
      errors.push('Preview must be 500 characters or less');
    }

    if (!formData.content?.trim()) {
      errors.push('Content is required');
    }

    // Optional field validation
    if (formData.meta_description && formData.meta_description.length > 160) {
      errors.push('Meta description must be 160 characters or less');
    }

    if (formData.featured_image_url && !isValidUrl(formData.featured_image_url)) {
      errors.push('Featured image must be a valid URL');
    }

    if (formData.slug) {
      const slug = formData.slug.trim();
      if (!/^[a-z0-9-]+$/.test(slug)) {
        errors.push('Slug can only contain lowercase letters, numbers, and hyphens');
      }
      if (slug.length > 200) {
        errors.push('Slug must be 200 characters or less');
      }
    }

    return errors;
  },

  // Get blog status info
  getStatusInfo: (blog) => {
    if (blog.is_published && blog.published_at) {
      return {
        status: 'Published',
        color: 'green',
        icon: '✅',
        description: `Published on ${blogHelpers.formatDate(blog.published_at, 'medium')}`
      };
    } else if (blog.is_published && !blog.published_at) {
      return {
        status: 'Publishing...',
        color: 'yellow',
        icon: '⏳',
        description: 'Being published'
      };
    } else {
      return {
        status: 'Draft',
        color: 'gray',
        icon: '📝',
        description: 'Not published'
      };
    }
  }
};

// ============= ERROR HANDLERS =============

// General error handler
export const handleApiError = (error, context = 'API operation') => {
  console.error(`API error in ${context}:`, error);
  
  // Network error
  if (!error.response) {
    return 'Network error. Please check your internet connection.';
  }
  
  // HTTP status code errors
  const status = error.response.status;
  const message = error.response.data?.detail || error.response.data?.message;
  
  switch (status) {
    case 400:
      return message || 'Invalid request. Please check your input.';
    case 401:
      return 'Authentication required. Please log in again.';
    case 403:
      return 'Access denied. You do not have permission for this action.';
    case 404:
      return 'Resource not found.';
    case 422:
      return message || 'Invalid data provided.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
      return 'Server error. Please try again later.';
    case 503:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      return message || `Request failed with status ${status}. Please try again.`;
  }
};

// Callback-specific error handler
export const handleCallbackError = (error, context = 'callback operation') => {
  const baseMessage = handleApiError(error, context);
  
  // Add callback-specific context
  if (error.response?.status === 422) {
    return 'Invalid callback data. Please check the callback parameters.';
  }
  
  return baseMessage;
};

// Blog-specific error handler
export const handleBlogError = (error, context = 'blog operation') => {
  const baseMessage = handleApiError(error, context);
  
  // Add blog-specific context
  if (error.response?.status === 409) {
    return 'A blog with this slug already exists. Please use a different slug.';
  }
  
  if (error.response?.status === 422) {
    const details = error.response.data?.detail;
    if (typeof details === 'object' && details.length) {
      return `Invalid blog data: ${details.join(', ')}`;
    }
    return 'Invalid blog data. Please check your input.';
  }
  
  return baseMessage;
};

// ============= UTILITY FUNCTIONS =============

// URL validation
const isValidUrl = (string) => {
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

// Create query string from object
export const createQueryString = (params) => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => 
      value !== null && value !== undefined && value !== ''
    )
  );
  return new URLSearchParams(filteredParams).toString();
};

// Format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Debounce function for search inputs
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Export the configured axios instance as default
export default api;